import { defineComponent as _defineComponent } from 'vue'
import CcAlert from '@/views/Automation/CampaignCreatorV2/components/CcAlert/CcAlert.ts.vue';
import CcBudget from '@/views/Automation/CampaignCreatorV2/components/CcBudget/CcBudget.ts.vue';
import CcCard from '@/views/Automation/CampaignCreatorV2/components/CcCard/CcCard.ts.vue';
import CcCardItem from '@/views/Automation/CampaignCreatorV2/components/CcCard/CcCardItem.ts.vue';
import CcCheckbox from '@/views/Automation/CampaignCreatorV2/components/CcCheckbox/CcCheckbox.ts.vue';
import CcDateTimePicker from '@/views/Automation/CampaignCreatorV2/components/CcDatePicker/CcDateTimePicker.ts.vue';
import CcError from '@/views/Automation/CampaignCreatorV2/components/CcError/CcError.ts.vue';
import CcIcon from '@/views/Automation/CampaignCreatorV2/components/CcIcon/CcIcon.ts.vue';
import CcActionsPopover from '@/views/Automation/CampaignCreatorV2/components/CcPopover/CcActionsPopover.ts.vue';
import {
  AddBudgetTypeDropdownEvent,
  ViewLevelActionsEvent,
} from '@/views/Automation/CampaignCreatorV2/components/CcPopover/helpers';
import CcVariableTags from '@/views/Automation/CampaignCreatorV2/components/CcVariableTags/CcVariableTags.ts.vue';
import useAbGroups from '@/views/Automation/CampaignCreatorV2/store/AbGroups';
import useAdSetStore from '@/views/Automation/CampaignCreatorV2/store/AdSet';
import useCampaignStore from '@/views/Automation/CampaignCreatorV2/store/Campaign';
import useNavigationStore from '@/views/Automation/CampaignCreatorV2/store/Navigation';
import { InternalABVariable, SupportedEntities } from '@/views/Automation/CampaignCreatorV2/store/Types';
import { CardLayer } from '@/views/Automation/CampaignCreatorV2/types';
import { AdSetAbGroups } from '@/views/Automation/CampaignCreatorV2/validation/AdSet';
import { Position } from '@sh/types';
import moment from 'moment';
import { storeToRefs } from 'pinia';
import uid from 'uid';
import { computed, onMounted } from 'vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'BudgetAndSchedule.ts',
  setup(__props) {

const campaignStore = useCampaignStore();
const campaignGetters = storeToRefs(campaignStore);
const adSetStore = useAdSetStore();
const adSetGetters = storeToRefs(adSetStore);
const { getField } = adSetGetters;

// new values
const startTime = computed(() => adSetGetters.getField.value('start_time')?.value?.[0]);
const endTime = computed(() => adSetGetters.getField.value('end_time')?.value?.[0]);
const dailySpendCap = computed(() => {
  const cap = adSetGetters.getField.value('daily_spend_cap')?.value?.[0];
  return {
    ...(cap || {}),
    value: cap?.value ? cap.value / 100 : cap?.value,
  };
});
const dailyMinSpendTarget = computed(() => {
  const cap = adSetGetters.getField.value('daily_min_spend_target')?.value?.[0];
  return {
    ...(cap || {}),
    value: cap?.value ? cap.value / 100 : cap?.value,
  };
});

const isBudgetManagedInCampaignLevel = computed(() => campaignGetters.getIsCampaignBudgetActive.value);
const activeTab = computed(() => '1');
const isSpendCapEnabled = computed(() => adSetGetters.isSpendCapEnabled.value);

const dailyBudget = computed(() => {
  const daily = adSetGetters.getField.value('daily_budget');
  return { ...daily, value: (daily?.value || []).map((e) => ({ ...e, value: e.value / 100 })) };
});

const lifetimeBudget = computed(() => {
  const daily = adSetGetters.getField.value('lifetime_budget');
  return { ...daily, value: (daily?.value || []).map((e) => ({ ...e, value: e.value / 100 })) };
});

function addNewBudget(event: ViewLevelActionsEvent) {
  const field = event === AddBudgetTypeDropdownEvent.ADD_DAILY_BUDGET ? 'daily_budget' : 'lifetime_budget';
  const variableId = uid();

  adSetStore.addOrEditVariable(field, {
    field,
    value: 25 * 100,
    variableId,
    name: `Budget Variant ${variableId}`,
  });
}

function updateBudget(value: { budget_type: string; value: string }, key: string) {
  const budgetType = value.budget_type === 'daily_budget' ? 'lifetime_budget' : 'daily_budget';
  adSetStore.removeVariable(budgetType as 'daily_budget', key);
  adSetStore.addOrEditVariable(value.budget_type as 'daily_budget', {
    field: value.budget_type,
    value: parseFloat(value.value) * 100,
    variableId: key,
  });
}

function changeStartTime({ date }: { date: Date }) {
  adSetStore.addOrEditVariable('start_time', { field: 'start_time', value: date, variableId: 'start_time' });
}

function changeEndTime({ date: value }: { date: Date }) {
  adSetStore.addOrEditVariable('end_time', { field: 'end_time', value, variableId: 'end_time' });
}

function changeEnableEndDate(value: boolean, variableId: string) {
  if (value) {
    changeEndTime({ date: moment().add(30, 'days').toDate() });
  } else {
    adSetStore.removeVariable('end_time', variableId);
  }
}

function changeSpendCapManagement(value: boolean) {
  adSetStore.updateInternalMetadata('enableDailySpendCap', value);
}

function changeDailySpendCap(value: string) {
  const newVal = parseFloat(value) * 100;
  adSetStore.addOrEditVariable('daily_spend_cap', {
    field: 'daily_spend_cap',
    value: newVal,
    variableId: 'daily_spend_cap',
  });
}

function changeDailyMinSpendTarget(value: string) {
  const newVal = parseFloat(value) * 100;
  adSetStore.addOrEditVariable('daily_min_spend_target', {
    field: 'daily_min_spend_target',
    value: newVal,
    variableId: 'daily_min_spend_target',
  });
}

function handleRenameBudgetVariation({
  budget,
  newName,
}: {
  budget: InternalABVariable<any> & { field: 'daily_budget' | 'lifetime_budget' };
  newName: string;
}) {
  adSetStore.addOrEditVariableName(budget.field, budget.abGroup!, budget.variableId, newName);
}

function removeDailyBudget(variableId: string) {
  adSetStore.removeVariable('daily_budget', variableId);
}

function removeLifetimeBudget(variableId: string) {
  adSetStore.removeVariable('lifetime_budget', variableId);
}

function onNewGroupAdd() {
  if (!isBudgetManagedInCampaignLevel.value) {
    addNewBudget(AddBudgetTypeDropdownEvent.ADD_DAILY_BUDGET);
  }
}

onMounted(() => {
  // TODO: Has issues fix
  const abGroupStore = useAbGroups();
  const navigationStore = useNavigationStore();
  const current = navigationStore.getCurrentAbGroupFor(SupportedEntities.adset, AdSetAbGroups.BUDGET);
  if (!current) {
    abGroupStore.addAbGroup(SupportedEntities.adset, {
      description: '',
      name: 'New Group',
      entity: SupportedEntities.adset,
      typeId: AdSetAbGroups.BUDGET as any,
    });
  }
  const hasBudget = dailyBudget.value.value?.length > 0 || lifetimeBudget.value.value?.length > 0;
  if (!hasBudget && !isBudgetManagedInCampaignLevel.value) {
    addNewBudget(AddBudgetTypeDropdownEvent.ADD_DAILY_BUDGET);
  }
});

return { __sfc: true,campaignStore, campaignGetters, adSetStore, adSetGetters, getField, startTime, endTime, dailySpendCap, dailyMinSpendTarget, isBudgetManagedInCampaignLevel, activeTab, isSpendCapEnabled, dailyBudget, lifetimeBudget, addNewBudget, updateBudget, changeStartTime, changeEndTime, changeEnableEndDate, changeSpendCapManagement, changeDailySpendCap, changeDailyMinSpendTarget, handleRenameBudgetVariation, removeDailyBudget, removeLifetimeBudget, onNewGroupAdd, CcAlert, CcBudget, CcCard, CcCardItem, CcCheckbox, CcDateTimePicker, CcError, CcIcon, CcActionsPopover, AddBudgetTypeDropdownEvent, CcVariableTags, SupportedEntities, CardLayer, AdSetAbGroups, Position }
}

})